// CR051: Recipe Builder Tab - Step-by-step recipe editing
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Save, Plus, Trash2, GripVertical, ChevronDown, ChevronRight, 
  Clock, MousePointer, Camera, ArrowDown, AlertCircle, Check,
  X, Map, Scroll as ScrollIcon, Repeat
} from 'lucide-react';
import type { RecipeTemplate } from '@/pages/admin/FlyerCaptureConsole';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface RecipeBuilderTabProps {
  recipeId: string;
  recipes: RecipeTemplate[];
  merchantOverrides: Record<string, unknown>;
  onRecipeUpdate: (recipe: RecipeTemplate) => void;
}

interface RecipeStep {
  type: string;
  params: Record<string, unknown>;
}

const STEP_TYPES = [
  { type: 'WAIT', label: 'Wait', icon: Clock, description: 'Wait for specified milliseconds' },
  { type: 'CLICK', label: 'Click', icon: MousePointer, description: 'Click on an element' },
  { type: 'ACCEPT_COOKIES_IF_PRESENT', label: 'Accept Cookies', icon: Check, description: 'Accept cookie consent if visible' },
  { type: 'CLOSE_MODAL_IF_PRESENT', label: 'Close Modal', icon: X, description: 'Close popup/modal if visible' },
  { type: 'SET_LOCATION_POSTAL_CODE', label: 'Set Location', icon: Map, description: 'Enter postal code for location gate' },
  { type: 'CONFIRM_STORE_IF_PRESENT', label: 'Confirm Store', icon: Check, description: 'Confirm store selection if prompted' },
  { type: 'DETECT_FLYER_VIEWER_TYPE', label: 'Detect Viewer', icon: AlertCircle, description: 'Auto-detect flyer viewer type' },
  { type: 'ASSERT_VIEWER_READY', label: 'Assert Viewer Ready', icon: Check, description: 'Wait for viewer to be ready' },
  { type: 'CAPTURE_PAGE_SCREENSHOT', label: 'Capture Screenshot', icon: Camera, description: 'Take a screenshot of the current page' },
  { type: 'LOOP_NEXT_PAGE', label: 'Loop: Next Page', icon: ArrowDown, description: 'Loop through pages using Next button' },
  { type: 'LOOP_SCROLL_CAPTURE', label: 'Loop: Scroll Capture', icon: ScrollIcon, description: 'Loop through content by scrolling' },
  { type: 'LOOP_SCROLL_DOWN_CAPTURE', label: 'Loop: Scroll Down Capture', icon: Repeat, description: 'Scroll down by page height, wait, and capture screenshot in a loop' },
  { type: 'SCROLL', label: 'Scroll', icon: ScrollIcon, description: 'Scroll the page' },
  { type: 'STOP_WHEN_PAGE_REPEATS', label: 'Stop on Repeat', icon: AlertCircle, description: 'Stop when duplicate page detected' },
  { type: 'STOP_AFTER_N_PAGES', label: 'Stop After N Pages', icon: AlertCircle, description: 'Stop after N pages captured' },
];

const getStepIcon = (type: string) => {
  const stepType = STEP_TYPES.find(s => s.type === type);
  return stepType?.icon || AlertCircle;
};

const StepEditor = ({ 
  step, 
  index, 
  onUpdate, 
  onDelete 
}: { 
  step: RecipeStep; 
  index: number;
  onUpdate: (step: RecipeStep) => void; 
  onDelete: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const StepIcon = getStepIcon(step.type);
  const stepDef = STEP_TYPES.find(s => s.type === step.type);

  const updateParam = (key: string, value: unknown) => {
    onUpdate({ ...step, params: { ...step.params, [key]: value } });
  };

  return (
    <div className="border rounded-lg">
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
        <Badge variant="outline" className="font-mono text-xs">{index + 1}</Badge>
        <StepIcon className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium flex-1">{stepDef?.label || step.type}</span>
        {Object.keys(step.params).length > 0 && (
          <span className="text-xs text-muted-foreground">
            {Object.entries(step.params).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ').slice(0, 40)}
          </span>
        )}
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </div>

      {isExpanded && (
        <div className="p-4 pt-0 space-y-4 border-t">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Step Type</Label>
              <Select
                value={step.type}
                onValueChange={(v) => onUpdate({ type: v, params: {} })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STEP_TYPES.map((st) => (
                    <SelectItem key={st.type} value={st.type}>
                      <div className="flex items-center gap-2">
                        <st.icon className="h-4 w-4" />
                        {st.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dynamic parameters based on step type */}
          {step.type === 'WAIT' && (
            <div className="space-y-2">
              <Label>Wait Time (ms)</Label>
              <Input
                type="number"
                value={(step.params.ms as number) || 1000}
                onChange={(e) => updateParam('ms', parseInt(e.target.value))}
              />
            </div>
          )}

          {step.type === 'CLICK' && (
            <>
              <div className="space-y-2">
                <Label>Selector Key (from overrides)</Label>
                <Input
                  value={(step.params.selector_key as string) || ''}
                  onChange={(e) => updateParam('selector_key', e.target.value)}
                  placeholder="e.g., next_button_selector"
                />
              </div>
              <div className="space-y-2">
                <Label>Or Direct Selector</Label>
                <Input
                  value={(step.params.selector as string) || ''}
                  onChange={(e) => updateParam('selector', e.target.value)}
                  placeholder="e.g., button.next"
                />
              </div>
              <div className="space-y-2">
                <Label>Timeout (ms)</Label>
                <Input
                  type="number"
                  value={(step.params.timeout_ms as number) || 5000}
                  onChange={(e) => updateParam('timeout_ms', parseInt(e.target.value))}
                />
              </div>
            </>
          )}

          {step.type === 'CAPTURE_PAGE_SCREENSHOT' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`full_page_${index}`}
                  checked={(step.params.full_page as boolean) || false}
                  onChange={(e) => updateParam('full_page', e.target.checked)}
                />
                <Label htmlFor={`full_page_${index}`}>Capture Full Page</Label>
              </div>
              <div className="space-y-2">
                <Label>Target Selector (optional)</Label>
                <Input
                  value={(step.params.target_selector as string) || ''}
                  onChange={(e) => updateParam('target_selector', e.target.value)}
                  placeholder="e.g., .flyer-viewer"
                />
              </div>
            </div>
          )}

          {step.type === 'LOOP_NEXT_PAGE' && (
            <div className="space-y-2">
              <Label>Method</Label>
              <Select
                value={(step.params.method as string) || 'button'}
                onValueChange={(v) => updateParam('method', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="button">Button Click</SelectItem>
                  <SelectItem value="keyboard">Keyboard Arrow</SelectItem>
                  <SelectItem value="swipe">Swipe Gesture</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {step.type === 'LOOP_SCROLL_DOWN_CAPTURE' && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                This step scrolls the page down by one viewport height, waits, captures a screenshot, and repeats until the end is detected or max iterations reached.
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Wait After Scroll (ms)</Label>
                  <Input
                    type="number"
                    value={(step.params.wait_ms as number) || 1000}
                    onChange={(e) => updateParam('wait_ms', parseInt(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scroll Amount</Label>
                  <Select
                    value={(step.params.scroll_amount as string) || 'viewport'}
                    onValueChange={(v) => updateParam('scroll_amount', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewport">Full Viewport Height</SelectItem>
                      <SelectItem value="half">Half Viewport Height</SelectItem>
                      <SelectItem value="custom">Custom Pixels</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(step.params.scroll_amount as string) === 'custom' && (
                <div className="space-y-2">
                  <Label>Custom Scroll Distance (px)</Label>
                  <Input
                    type="number"
                    value={(step.params.scroll_pixels as number) || 800}
                    onChange={(e) => updateParam('scroll_pixels', parseInt(e.target.value))}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Iterations</Label>
                  <Input
                    type="number"
                    value={(step.params.max_iterations as number) || 30}
                    onChange={(e) => updateParam('max_iterations', parseInt(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Detection</Label>
                  <Select
                    value={(step.params.end_detection as string) || 'scroll_stall'}
                    onValueChange={(v) => updateParam('end_detection', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scroll_stall">Scroll Position Unchanged</SelectItem>
                      <SelectItem value="content_hash">Content Hash Unchanged</SelectItem>
                      <SelectItem value="max_only">Max Iterations Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Target Container Selector (optional)</Label>
                <Input
                  value={(step.params.container_selector as string) || ''}
                  onChange={(e) => updateParam('container_selector', e.target.value)}
                  placeholder="e.g., .flyer-content, #main-viewer (defaults to window)"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`capture_full_page_${index}`}
                  checked={(step.params.capture_full_page as boolean) || false}
                  onChange={(e) => updateParam('capture_full_page', e.target.checked)}
                />
                <Label htmlFor={`capture_full_page_${index}`}>Capture Full Page (vs. viewport only)</Label>
              </div>
            </div>
          )}

          {step.type === 'STOP_AFTER_N_PAGES' && (
            <div className="space-y-2">
              <Label>Max Pages</Label>
              <Input
                type="number"
                value={(step.params.n as number) || 30}
                onChange={(e) => updateParam('n', parseInt(e.target.value))}
              />
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Step
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const RecipeBuilderTab = ({ 
  recipeId, 
  recipes, 
  merchantOverrides,
  onRecipeUpdate 
}: RecipeBuilderTabProps) => {
  const recipe = recipes.find(r => r.id === recipeId);
  const [steps, setSteps] = useState<RecipeStep[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (recipe) {
      setSteps(recipe.steps as RecipeStep[]);
      setHasChanges(false);
    }
  }, [recipe]);

  if (!recipe) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Recipe not found
      </div>
    );
  }

  const handleStepUpdate = (index: number, step: RecipeStep) => {
    const newSteps = [...steps];
    newSteps[index] = step;
    setSteps(newSteps);
    setHasChanges(true);
  };

  const handleStepDelete = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleAddStep = () => {
    setSteps([...steps, { type: 'WAIT', params: { ms: 1000 } }]);
    setHasChanges(true);
  };

  const handleSave = () => {
    onRecipeUpdate({ ...recipe, steps: steps as unknown[] });
    setHasChanges(false);
  };

  const usedByCount = recipes.filter(r => r.id !== recipe.id).length; // Placeholder

  return (
    <div className="space-y-6">
      {/* Recipe Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                {recipe.recipe_name}
                {recipe.is_system_template && (
                  <Badge variant="secondary">System Template</Badge>
                )}
              </CardTitle>
              <CardDescription>{recipe.description}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => toast.info('Save as new recipe coming in Phase 2')}
              >
                Fork Recipe
              </Button>
              <Button size="sm" onClick={handleSave} disabled={!hasChanges}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Steps List */}
      <Card>
        <CardHeader>
          <CardTitle>Recipe Steps</CardTitle>
          <CardDescription>
            {steps.length} steps • Click to expand and edit
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="max-h-[500px]">
            <div className="space-y-2">
              {steps.map((step, index) => (
                <StepEditor
                  key={index}
                  step={step}
                  index={index}
                  onUpdate={(s) => handleStepUpdate(index, s)}
                  onDelete={() => handleStepDelete(index)}
                />
              ))}
            </div>
          </ScrollArea>
          
          <Button variant="outline" className="w-full mt-4" onClick={handleAddStep}>
            <Plus className="h-4 w-4 mr-2" />
            Add Step
          </Button>
        </CardContent>
      </Card>

      {/* Recipe Defaults */}
      <Accordion type="single" collapsible>
        <AccordionItem value="defaults">
          <Card>
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <CardTitle className="text-lg">Recipe Defaults</CardTitle>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="pt-0">
                <pre className="text-xs font-mono bg-muted p-4 rounded-lg overflow-auto max-h-64">
                  {JSON.stringify(recipe.recipe_defaults, null, 2)}
                </pre>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>
      </Accordion>

      {/* Success Criteria */}
      <Card>
        <CardHeader>
          <CardTitle>Success Criteria</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="text-xs font-mono bg-muted p-4 rounded-lg">
            {JSON.stringify(recipe.success_criteria, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};
